import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { AuthProvider } from "./lib/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AuditIntake from "./pages/AuditIntake";
import ManualRun from "./pages/ManualRun";
import ManualScore from "./pages/ManualScore";
import Processing from "./pages/Processing";
import Snapshot from "./pages/Snapshot";
import Dashboard from "./pages/Dashboard";
import MySnapshot from "./pages/MySnapshot";
import Pipeline from "./pages/Pipeline";
import Admin from "./pages/Admin";
import ResearchHub from "./pages/research/ResearchHub";
import ResearchCategory from "./pages/research/ResearchCategory";
import ResearchArticle from "./pages/research/ResearchArticle";
import ResearchMethodology from "./pages/research/ResearchMethodology";
import AdminPublications from "./pages/admin/AdminPublications";
import AdminPublicationForm from "./pages/admin/AdminPublicationForm";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminBenchmark from "./pages/admin/AdminBenchmark";
import LLMInfo from "./pages/LLMInfo";
import LandingPage from "./pages/LandingPage";
import AIOperations from "./pages/AIOperations";
import BookEfficiencyReview from "./pages/BookEfficiencyReview";
import BuyAssessment, { AssessmentThankYou } from "./pages/BuyAssessment";
import AdminOperations from "./pages/admin/AdminOperations";

// Sitewide Organization + WebSite JSON-LD — injected once into <head>.
const SITE_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "The Entity Agency",
    "url": "https://theentityagency.com",
    "logo": "https://theentityagency.com/logo.png",
    "slogan": "AI Visibility Intelligence",
    "description":
      "The Entity Agency is the category authority on AI Visibility Intelligence — the measurement and optimization of how ChatGPT, Claude, Gemini, and Perplexity describe and recommend businesses.",
    "knowsAbout": [
      "AI Visibility", "AI Visibility Audit", "AI Visibility Score",
      "Entity SEO", "AI Search Optimization", "AI Brand Visibility",
      "ChatGPT Visibility", "Claude Visibility", "Gemini Visibility", "Perplexity Visibility",
    ],
    "contactPoint": [{
      "@type": "ContactPoint",
      "email": "contact@theentityagency.com",
      "contactType": "customer support",
      "url": "https://theentityagency.com",
    }],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "The Entity Agency",
    "url": "https://theentityagency.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://theentityagency.com/research?q={query}",
      "query-input": "required name=query",
    },
  },
];

function RedirectToResearchSlug({ category }) {
  const { slug } = useParams();
  return <Navigate to={`/research/${category}/${slug}`} replace />;
}

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        {SITE_JSONLD.map((blob, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(blob)}</script>
        ))}
      </Helmet>
      <div className="App grain">
        <BrowserRouter>
          <AuthProvider>
            <Navbar />
            <main className="relative z-10">
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/audit/new" element={<ProtectedRoute><AuditIntake /></ProtectedRoute>} />
                <Route path="/audit/:id/manual" element={<ProtectedRoute adminOnly><ManualRun /></ProtectedRoute>} />
                <Route path="/audit/:id/score" element={<ProtectedRoute adminOnly><ManualScore /></ProtectedRoute>} />
                <Route path="/processing/:id" element={<ProtectedRoute><Processing /></ProtectedRoute>} />
                <Route path="/snapshot/:id" element={<ProtectedRoute><Snapshot /></ProtectedRoute>} />
                <Route path="/my-snapshot" element={<ProtectedRoute><MySnapshot /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
                <Route path="/pipeline" element={<ProtectedRoute adminOnly><Pipeline /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />

                {/* SEO / Authority landing pages */}
                <Route path="/ai-visibility-audit" element={<LandingPage slug="ai-visibility-audit" />} />
                <Route path="/ai-visibility-score" element={<LandingPage slug="ai-visibility-score" />} />
                <Route path="/entity-seo" element={<LandingPage slug="entity-seo" />} />
                <Route path="/ai-search-optimization" element={<LandingPage slug="ai-search-optimization" />} />
                <Route path="/chatgpt-visibility" element={<LandingPage slug="chatgpt-visibility" />} />
                <Route path="/claude-visibility" element={<LandingPage slug="claude-visibility" />} />
                <Route path="/perplexity-visibility" element={<LandingPage slug="perplexity-visibility" />} />
                <Route path="/gemini-visibility" element={<LandingPage slug="gemini-visibility" />} />
                <Route path="/construction-ai-visibility" element={<LandingPage slug="construction-ai-visibility" />} />
                <Route path="/manufacturing-ai-visibility" element={<LandingPage slug="manufacturing-ai-visibility" />} />
                <Route path="/professional-services-ai-visibility" element={<LandingPage slug="professional-services-ai-visibility" />} />
                <Route path="/automotive-ai-visibility" element={<LandingPage slug="automotive-ai-visibility" />} />
                <Route path="/healthcare-ai-visibility" element={<LandingPage slug="healthcare-ai-visibility" />} />
                <Route path="/about-ai-visibility" element={<LandingPage slug="about-ai-visibility" />} />

                {/* /resources and /case-studies redirect into the merged Research publication */}
                <Route path="/resources" element={<Navigate to="/research/resources" replace />} />
                <Route path="/resources/:slug" element={<RedirectToResearchSlug category="resources" />} />
                <Route path="/case-studies" element={<Navigate to="/research/case-studies" replace />} />
                <Route path="/case-studies/:slug" element={<RedirectToResearchSlug category="case-studies" />} />

                {/* Research Publication */}
                <Route path="/research" element={<ResearchHub />} />
                <Route path="/research/methodology" element={<ResearchMethodology />} />
                <Route path="/research/:category" element={<ResearchCategory />} />
                <Route path="/research/:category/:slug" element={<ResearchArticle />} />

                {/* Admin CMS */}
                <Route path="/admin/publications" element={<ProtectedRoute adminOnly><AdminPublications /></ProtectedRoute>} />
                <Route path="/admin/publications/new" element={<ProtectedRoute adminOnly><AdminPublicationForm /></ProtectedRoute>} />
                <Route path="/admin/publications/:pubId/edit" element={<ProtectedRoute adminOnly><AdminPublicationForm /></ProtectedRoute>} />
                <Route path="/admin/payments" element={<ProtectedRoute adminOnly><AdminPayments /></ProtectedRoute>} />
                <Route path="/admin/benchmarks" element={<ProtectedRoute adminOnly><AdminBenchmark /></ProtectedRoute>} />
                <Route path="/admin/operations" element={<ProtectedRoute adminOnly><AdminOperations /></ProtectedRoute>} />

                {/* ── AI Operations Integration (public) ── */}
                <Route path="/ai-operations" element={<AIOperations />} />
                <Route path="/ai-operations/thank-you" element={<AssessmentThankYou />} />
                <Route path="/book-efficiency-review" element={<BookEfficiencyReview />} />
                <Route path="/buy-assessment" element={<BuyAssessment />} />
                <Route path="/admin/benchmarks/:benchmarkId" element={<ProtectedRoute adminOnly><AdminBenchmark /></ProtectedRoute>} />
                <Route path="/llm"      element={<LLMInfo />} />
                <Route path="/llm-info" element={<LLMInfo />} />
                <Route path="/ai-info"  element={<LLMInfo />} />
              </Routes>
            </main>
            <Footer />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
